import React, { useContext, useEffect, useState } from "react";
import { InventoryContent } from "../../utils/content";
import { inventoryOptions, selectAll, transformItem } from "../../utils/utils";
import { ListWrapper } from "../../assets/styles/common.style";
import { Footer, Header, SelectedItemsFooter } from "../../components";
import {
  Loader,
  Dropdown,
  InnerPageHead,
  Pagination,
  CardItem,
  Button,
} from "../../ui";
import { useNavigate } from "react-router-dom";
import { NearContext } from "../../contexts/NearWallet";
import {
  Container,
  InnerPageWrapper,
  ListSmall,
  Wrapper,
} from "../../assets/styles/common.style";
import { get } from "../../utils/api";
import { updateUserBalance } from "../../services/UserService";
import { useDispatch } from "react-redux";

const PAGE_LIMIT = 40;

export const Inventory = () => {
  const { currentUser, mainContract, ftContract } = useContext(NearContext);
  const dispatch = useDispatch();
  const [isReady, setIsReady] = useState(false);
  const [userInventory, setUserInventory] = useState([0, []]); // [<count>, [<arrayOfItems>]]
  const [currentPage, setCurrentPage] = useState(1);
  const [filterType, setFilterType] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const navigate = useNavigate();

  async function fetchUserInventory(currentPage, itemType) {
    let requestParams = {
      account_id: currentUser,
      page_num: currentPage,
      page_limit: PAGE_LIMIT,
    };
    if (itemType) {
      requestParams["filter_type"] = itemType;
    }
    let items = await mainContract.userInventoryItems(requestParams);

    const itemsResp = await get("api/items", {
      ids: items[1].map((item) => item.token_id),
      types: items[1].map((item) => item.nft_type),
    });

    let itemsDB = itemsResp.data;

    if (itemsDB) {
      // Convert price from Yocto NEAR and add durability
      items[1] = items[1]
        .filter((item) => item.owner_id === currentUser)
        .map((item) => {
          let itemDB = itemsDB.find((i) => i.itemId === item.token_id);

          let transformedItem = transformItem(item);
          transformedItem.durabilityMin = itemDB?.durabilityMin;
          transformedItem.durabilityMax = itemDB?.durabilityMax;
          transformedItem.level = itemDB?.level;

          return transformedItem;
        });

      setUserInventory(items);
    }

    setIsReady(true);
  }

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const page = JSON.parse(searchParams.has("page"))
      ? parseInt(searchParams.get("page"))
      : currentPage;
    const itemType = JSON.parse(searchParams.has("type"))
      ? searchParams.get("type")
      : filterType;

    setCurrentPage(page);
    setFilterType(itemType);
    fetchUserInventory(page, itemType);
  }, []);

  useEffect(() => {
    if (isReady) {
      setCurrentPage(1);
      fetchUserInventory(1, filterType);
      navigate(buildUrl());
    }
  }, [filterType]);

  useEffect(() => navigate(buildUrl()), [currentPage]);

  const buildUrl = () => {
    let url = `/inventory?page=${currentPage}`;
    if (filterType) url = `${url}&type=${filterType}`;
    return url;
  };

  const onPageChanged = (page) => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setCurrentPage(page);
    fetchUserInventory(page, filterType);
  };

  const rmFromMarket = async (item) => {
    setIsReady(false);
    await mainContract.removeFromMarket(item.token_id, item.nft_type);
    fetchUserInventory(currentPage, filterType);
  };

  const handleReloadData = () => {
    setSelectedItems([]);
    fetchUserInventory(currentPage, filterType);
    updateUserBalance(dispatch, ftContract, currentUser);
  };

  const selectItem = (item) => {
    const findItem = selectedItems.findIndex(
      (value) => value.token_id === item.token_id
    );
    if (findItem < 0)
      return setSelectedItems((prevState) => [...prevState, item]);
    return setSelectedItems(selectedItems.filter((_, ind) => findItem !== ind));
  };

  const isSelected = (itemId) =>
    selectedItems.filter((item) => item.token_id === itemId).length > 0;

  return (
    <InnerPageWrapper>
      <Header />

      <Wrapper>
        <Container className="text-white text-center mt-6">
          <InnerPageHead
            title={InventoryContent.title}
            description={InventoryContent.description}
          />

          {isReady ? (
            <>
              <div className="flex justify-between mt-8 z-30 relative">
                <div className="basis-1/2 text-lg text-left pt-2 pl-1">
                  Available:
                  <span
                    className="ml-2 font-semibold text-amber-600 cursor-pointer"
                    onClick={() =>
                      selectAll(
                        selectedItems,
                        setSelectedItems,
                        userInventory[1]
                      )
                    }
                  >
                    {userInventory[0]} item{userInventory[0] > 1 ? "s" : ""}
                  </span>
                </div>

                <div className="basis-1/2 z-10 text-right">
                  <div className="inline-block mr-1">
                    <Dropdown
                      title="Item Type"
                      selected={filterType}
                      options={inventoryOptions(setFilterType)}
                    />
                  </div>
                </div>
              </div>

              <ListWrapper>
                {userInventory[0] > 0 ? (
                  <ListSmall>
                    {userInventory[1]?.map((item) => (
                      <CardItem
                        isItem
                        item={item}
                        key={item.token_id}
                        rmFromMarket={() => rmFromMarket(item)}
                        handleSelect={() => selectItem(item)}
                        isSelected={isSelected(item.token_id)}
                      />
                    ))}
                  </ListSmall>
                ) : (
                  <div>You don't have {filterType} Inventory items.</div>
                )}
              </ListWrapper>

              {filterType && (
                <div className="mt-10">
                  <a
                    className="link cursor-pointer"
                    onClick={() => setFilterType(null)}
                  >
                    Reset Filters
                  </a>
                </div>
              )}

              <div className="mb-8">
                <Pagination
                  total={parseInt(userInventory[0])}
                  limit={PAGE_LIMIT}
                  selectedPage={currentPage}
                  onPageChanged={onPageChanged}
                />
              </div>
            </>
          ) : (
            <Loader />
          )}
        </Container>
      </Wrapper>

      {selectedItems.length > 0 && (
        <SelectedItemsFooter
          upgradable
          selectedItems={selectedItems}
          nftType="Inventory"
          deselectItem={selectItem}
          setSelectedItems={setSelectedItems}
          items={userInventory[1]}
          handleReloadData={handleReloadData}
        />
      )}

      <Footer />
    </InnerPageWrapper>
  );
};
